import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ConnectionUserWithConnectionInvite,
  acceptConnectionRequest,
  ignoreConnection,
  useAppDispatch,
  withAsyncThunkErrorHandling,
  withThunkErrorHandling,
} from "@lib/store";
import { Avatar, AvatarStack, Button, GroupAvatar } from "@molecules";
import { H4 } from "@quarks";
import { format } from "date-fns";

const now = new Date();
export const ConnectionInviteItem = ({
  userWithConnection,
}: {
  userWithConnection: ConnectionUserWithConnectionInvite;
}) => {
  const {
    id: userId,
    directoryObjectId,
    name,
    email,
    initials,
    connection,
    isActive,
    imageHash,
    connectionId,
  } = userWithConnection;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dateKey = format(now, "yyyy-MM-dd");

  const canChangeConnectionStatus = useMemo(
    () => connection && !connection.isAccepting && !connection.isIgnoring && !connection.isDeleting,
    [connection],
  );

  const acceptConnectionText = t("connection:AcceptConnection");
  const ignoreConnectionText = t("connection:IgnoreConnection");
  const title = useMemo(() => (name ? name : email), [name, email]);
  const subtitle = isActive ? email : t("connection:ConnectionNotActive");

  const onAcceptConnection = useCallback(() => {
    dispatch(withThunkErrorHandling(() => acceptConnectionRequest({ connectionId: connectionId, userId: userId })));
  }, [dispatch, connectionId, userId]);

  const onIgnoreConnection = useCallback(() => {
    dispatch(withAsyncThunkErrorHandling(() => ignoreConnection({ connectionId: connectionId, userId: userId })));
  }, [connectionId, dispatch, userId]);

  return (
    <div className="grid grid-cols-[auto_1fr] gap-4 items-center w-full py-4 last:pb-0">
      <Avatar
        user={userWithConnection}
        size="large"
        shouldShowCheckIn
        className="shadow-mapiq-small"
      />

      <div className="flex flex-col">
        <H4>{title}</H4>
        <p className="text-mapiq-black-500">{subtitle}</p>
      </div>

      <div className=" col-start-2 flex items-center gap-4">
        <Button
          button="secondary"
          size="small"
          loading={connection.isIgnoring}
          disabled={!canChangeConnectionStatus}
          onClick={onIgnoreConnection}>
          {ignoreConnectionText}
        </Button>
        <Button
          button="primary"
          size="small"
          loading={connection.isAccepting}
          disabled={!canChangeConnectionStatus}
          onClick={onAcceptConnection}>
          {acceptConnectionText}
        </Button>
      </div>
    </div>
  );
  return (
    <div className=" py-4 px-4 flex justify-between bg-white">
      <div className="flex-1 flex justify-between gap-4">
        <div className="flex-1 flex items-center gap-4">
          <div className="flex-shrink-0">
            <Avatar
              user={userWithConnection}
              size="large"
              shouldShowCheckIn
            />
          </div>
          <div className="flex-1 max-w-[200px]">
            <H4 className="truncate">{title}</H4>
            <p className="truncate">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-10" />
          <Button
            button="secondary"
            size="small"
            loading={connection.isIgnoring}
            disabled={!canChangeConnectionStatus}
            title={ignoreConnectionText}
            onClick={onIgnoreConnection}
          />
          <Button
            button="primary"
            size="small"
            loading={connection.isAccepting}
            disabled={!canChangeConnectionStatus}
            title={acceptConnectionText}
            onClick={onAcceptConnection}
          />
        </div>
      </div>
    </div>
  );
};
